import React, { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home/Home';
import InvalidRoute from './pages/Validate/InvalidRoute';
import VerifyNotesLink from './pages/Validate/VerifyNotesLink';
import StartSubmission from './pages/Validate/StartSubmission';
import TrialClassRecordings from './pages/UserPages/TrialClassRecordings';
import SubTopicListPage from './pages/QuestionPages/SubTopicListPage';
import QuestionsPage from './pages/QuestionPages';
import Payment from './pages/Payment';
import ManageSubscriptions from './pages/Payment/ManageSubscriptions';
import SignUp from './pages/Authentication/SignUp';
import Login from './pages/Authentication/Login';
import PortalPage from './pages/V2/Portal';
import PastPapersPage from './pages/PastPapers/PastPapersPage';
import { useAppSelector } from './hooks';
import BlogPage from './pages/BlogPage/BlogPage';
import ManageAccounts from './pages/V2/ManageAccounts';
import ForgotPassword from './pages/Authentication/ForgotPassword';
import ResetPassword from './pages/Authentication/ResetPassword';
import { getMetaTagsState } from './store/V2/MetaTagsSlice';
import { getMetaTags } from './store/V2/MetaTagsSlice/api';
import { getAppBaseUrlForTenant } from './helper';
import { getTenantsState } from './store/V2/TenantsSlice';

const BaseRoute: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // State
  const metaTagState = useAppSelector(getMetaTagsState);
  const tenantsState = useAppSelector(getTenantsState);

  const { metaTagData } = metaTagState;
  const { appTenant } = tenantsState;

  useEffect(() => {
    dispatch(
      getMetaTags({
        path: encodeURIComponent(location.pathname.replace(/[/]$/, '')),
      })
    );
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      {metaTagData && (metaTagData.title || metaTagData.description) ? (
        <Helmet>
          {metaTagData.title ? <title>{metaTagData.title}</title> : null}
          {metaTagData.description ? (
            <meta name="description" content={metaTagData.description} />
          ) : null}
        </Helmet>
      ) : null}
      {appTenant ? (
        <Helmet>
          <link
            rel="canonical"
            href={`${getAppBaseUrlForTenant(appTenant)}${
              location.pathname
            }`.replace(/\/$/, '')}
          />
        </Helmet>
      ) : null}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/portal/*" element={<PortalPage />} />
        <Route
          path="/trial-recording/:trialId"
          element={<TrialClassRecordings />}
        />
        <Route path="/dashboard/*" element={<Dashboard />} />

        <Route path="/:curriculum" element={<Home />} />
        <Route path="/verifyNotesLink" element={<VerifyNotesLink />} />
        <Route path="/startSubmission/:quizId" element={<StartSubmission />} />
        <Route path="/payment/:diagnosticId" element={<Payment />} />
        <Route path="/*" element={<InvalidRoute />} />
        <Route path="/subscriptions" element={<ManageSubscriptions />} />

        <Route path="/:curriculum/:subject" element={<SubTopicListPage />} />
        <Route path="/:curriculum/:subject/*" element={<QuestionsPage />} />
        <Route
          path="/dashboard/:curriculum/:subject/:subjectLevel/:subjectCode/past-papers"
          element={<PastPapersPage />}
        />
        <Route path="/topical-past-papers" element={<BlogPage />} />
        <Route path="/manage-accounts" element={<ManageAccounts />} />
      </Routes>
    </>
  );
};

export default BaseRoute;
